import React, { useState } from 'react';

const AgentDocumentVerificationCard = ({ 
  agent, 
  onViewDocument,
  onApproveDocuments,
  onRejectDocuments,
  isProcessing = false
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  if (!agent) return null;

  const docStatus = agent.documentVerificationStatus || (
    ((agent.gstNumber && agent.gstNumber !== 'N/A') || (agent.udyamAadharRegNo && agent.udyamAadharRegNo !== 'N/A'))
      ? (agent.approved ? 'APPROVED' : 'PENDING_APPROVAL')
      : 'NOT_SUBMITTED'
  );

  const getStatusBadge = () => {
    switch (docStatus) {
      case 'APPROVED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
            <i className="fas fa-check-circle mr-1.5 text-green-600"></i>
            Documents Approved
          </span>
        );
      case 'PENDING_APPROVAL':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse">
            <i className="fas fa-clock mr-1.5 text-amber-600"></i>
            Pending Verification
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
            <i className="fas fa-times-circle mr-1.5 text-red-600"></i>
            Documents Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
            <i className="fas fa-minus-circle mr-1.5 text-gray-500"></i>
            Not Submitted
          </span>
        );
    }
  };

  const handleConfirmReject = () => {
    if (onRejectDocuments) {
      onRejectDocuments(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  const documents = [
    {
      id: 'pan',
      title: 'PAN Card Document',
      numberLabel: 'PAN Number',
      number: agent.panNumber,
      image: agent.panCardImage,
      required: true,
      badgeColor: agent.panCardImage ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800',
      badgeText: agent.panCardImage ? 'Document Attached' : 'No Document Attached',
      icon: 'fa-id-card'
    },
    {
      id: 'gst',
      title: 'GST Certificate',
      numberLabel: 'GST Number',
      number: agent.gstNumber,
      image: agent.gstImage,
      required: false,
      badgeColor: agent.gstImage ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700',
      badgeText: agent.gstImage ? 'Document Attached' : 'Not Provided',
      icon: 'fa-file-invoice-dollar'
    },
    {
      id: 'udyam',
      title: 'Udyam Aadhaar Document',
      numberLabel: 'Udyam Aadhar Reg. No.',
      number: agent.udyamAadharRegNo,
      image: agent.udyamAadharImage,
      required: false,
      badgeColor: agent.udyamAadharImage ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700',
      badgeText: agent.udyamAadharImage ? 'Document Attached' : 'Not Provided',
      icon: 'fa-certificate'
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-600 text-white rounded-lg shadow-sm">
            <i className="fas fa-shield-alt text-lg"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Verification & Compliance Documents</h3>
            <p className="text-xs text-gray-600">
              Inspect uploaded PAN card, GST certificate, and Udyam Aadhaar images to verify agent credentials
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getStatusBadge()}
        </div>
      </div>

      {/* Rejection Reason Alert if status is REJECTED */}
      {docStatus === 'REJECTED' && agent.documentRejectionReason && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2.5">
          <i className="fas fa-exclamation-triangle text-red-600 mt-0.5 text-sm flex-shrink-0"></i>
          <div>
            <p className="text-xs font-bold text-red-800">Rejection Reason:</p>
            <p className="text-xs text-red-700">{agent.documentRejectionReason}</p>
          </div>
        </div>
      )}

      {/* Pending Approval Action Banner if actions are available */}
      {(onApproveDocuments || onRejectDocuments) && docStatus === 'PENDING_APPROVAL' && (
        <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
              <i className="fas fa-user-clock text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-amber-900">Document Review Pending</p>
              <p className="text-xs text-amber-700">Agent submitted documents for booking verification. Please inspect and approve or reject.</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onRejectDocuments && (
              <button
                type="button"
                disabled={isProcessing}
                onClick={() => setShowRejectModal(true)}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg text-red-700 bg-white border border-red-300 hover:bg-red-50 transition shadow-sm disabled:opacity-50"
              >
                <i className="fas fa-times mr-1"></i> Reject Documents
              </button>
            )}
            {onApproveDocuments && (
              <button
                type="button"
                disabled={isProcessing}
                onClick={onApproveDocuments}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg text-white bg-green-600 hover:bg-green-700 transition shadow-sm disabled:opacity-50"
              >
                <i className="fas fa-check mr-1"></i> Approve Documents
              </button>
            )}
          </div>
        </div>
      )}

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {documents.map((doc) => {
            const hasImage = Boolean(doc.image && doc.image.trim() !== '' && doc.image !== 'N/A');
            const hasNumber = Boolean(doc.number && doc.number.trim() !== '' && doc.number !== 'N/A' && doc.number !== '-');

            return (
              <div 
                key={doc.id}
                className={`rounded-xl border transition flex flex-col justify-between overflow-hidden ${
                  hasImage ? 'border-blue-200 bg-blue-50/20 shadow-sm' : 'border-gray-200 bg-gray-50/50'
                }`}
              >
                {/* Header */}
                <div className="p-4 border-b border-gray-100 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center">
                      <i className={`fas ${doc.icon} mr-1.5 text-blue-600`}></i>
                      {doc.title}
                    </span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex items-center ${doc.badgeColor}`}>
                      {hasImage ? (
                        <>
                          <i className="fas fa-check-circle mr-1"></i> Uploaded
                        </>
                      ) : (
                        doc.badgeText
                      )}
                    </span>
                  </div>

                  <div className="mt-1">
                    <p className="text-xs text-gray-500">{doc.numberLabel}</p>
                    <p className="text-sm font-bold text-gray-900 font-mono tracking-wide">
                      {hasNumber ? doc.number : <span className="text-gray-400 font-sans font-normal italic">Not Provided</span>}
                    </p>
                  </div>
                </div>

                {/* Preview Area */}
                <div className="p-4 flex-1 flex flex-col items-center justify-center min-h-[170px]">
                  {hasImage ? (
                    <div 
                      className="relative group w-full h-36 rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm cursor-pointer"
                      onClick={() => onViewDocument(doc.image, doc.title)}
                    >
                      <img 
                        src={doc.image} 
                        alt={doc.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-200"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center text-white transition duration-200 space-y-1">
                        <i className="fas fa-search-plus text-xl"></i>
                        <span className="text-xs font-semibold">Click to inspect</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center p-4 text-gray-400">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2 text-gray-400">
                        <i className={`fas ${doc.icon} text-lg`}></i>
                      </div>
                      <p className="text-xs font-medium text-gray-500">No Image Uploaded</p>
                      <p className="text-[11px] text-gray-400">Agent did not upload an image file</p>
                    </div>
                  )}
                </div>

                {/* Card Footer Actions */}
                <div className="p-3 bg-white border-t border-gray-100 flex items-center justify-between">
                  {hasImage ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onViewDocument(doc.image, doc.title)}
                        className="w-full inline-flex items-center justify-center px-3 py-2 text-xs font-semibold rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 transition shadow-sm"
                      >
                        <i className="fas fa-eye mr-1.5"></i>
                        Inspect Full Image
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 italic text-center w-full py-1">
                      Document file unavailable
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold">
                <i className="fas fa-times-circle text-lg"></i>
              </div>
              <div>
                <h4 className="text-base font-bold text-gray-900">Reject Submitted Documents</h4>
                <p className="text-xs text-gray-500">Please provide a reason so the agent knows what to fix.</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., GST certificate is blurred or name does not match business registration..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              />
            </div>

            <div className="flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-xs font-semibold rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReject}
                className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-red-600 hover:bg-red-700 transition shadow-sm"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDocumentVerificationCard;
